/**
 * 菜单数据转换工具
 * 统一处理菜单数据的树形结构转换和路由生成
 */

/**
 * 将一维菜单数组转换为树形结构
 * @param {Array} menuList - 一维菜单数组
 * @returns {Array} 树形结构的菜单数组
 */
export function buildMenuTree(menuList) {
    if (!Array.isArray(menuList) || menuList.length === 0) {
        return [];
    }

    const menuMap = new Map();
    const rootMenus = [];

    // 第一次遍历：创建菜单映射
    menuList.forEach((menu) => {
        menuMap.set(menu.id, { ...menu, children: [] });
    });

    // 第二次遍历：构建父子关系
    menuList.forEach((menu) => {
        const menuItem = menuMap.get(menu.id);
        if (
            menu.parentId == 0 ||
            menu.parentId === null ||
            menu.parentId === undefined
        ) {
            rootMenus.push(menuItem);
        } else {
            const parent = menuMap.get(menu.parentId);
            if (parent) {
                parent.children.push(menuItem);
            }
        }
    });

    // 递归排序
    function sortMenus(menus) {
        menus.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0));
        menus.forEach((menu) => {
            if (menu.children && menu.children.length > 0) {
                sortMenus(menu.children);
            }
        });
    }

    sortMenus(rootMenus);
    return rootMenus;
}

/**
 * 递归转换菜单树为路由配置
 * @param {Array} menuTree - 树形结构的菜单数组
 * @param {Object} options - 转换选项
 * @param {boolean} options.isDynamic - 是否为动态路由（影响meta标记）
 * @returns {Array} 路由配置数组
 */
export function convertMenuTreeToRoutes(menuTree, options = {}) {
    const { isDynamic = false } = options;
    const routes = [];

    // 组件导入缓存，提高性能
    const componentCache = new Map();

    /**
     * 动态导入组件的函数
     * @param {string} componentPath - 组件路径
     * @returns {Function} 动态导入函数
     */
    function dynamicImportComponent(componentPath) {
        // 支持对象/已实例化组件：直接返回组件本身
        if (typeof componentPath !== "string") {
            return componentPath;
        }

        // 检查缓存
        if (componentCache.has(componentPath)) {
            return componentCache.get(componentPath);
        }

        // 创建动态导入函数
        const importFunc = () => {
            try {
                // 构建完整的组件路径，确保Vite能正确解析
                const dynamicImport = import.meta.glob("/src/**/*.vue", {
                    eager: false,
                });
                const fullPath = `/src/${componentPath}`;

                if (dynamicImport[fullPath]) {
                    return dynamicImport[fullPath]();
                } else {
                    console.warn(`组件路径未找到: /src/${componentPath}`);
                    // 返回404页面作为回退
                    return import("@/views/NotFoundPage.vue");
                }
            } catch (error) {
                console.warn(`组件加载失败: @/${componentPath}`, error);
                // 返回404页面作为回退
                return import("@/views/NotFoundPage.vue");
            }
        };

        // 存入缓存
        componentCache.set(componentPath, importFunc);
        return importFunc;
    }

    // 获取默认的组件导入函数
    function getDefaultComponent(menuType, hasChildren) {
        // 纯目录，使用ParentView
        if (menuType === "M" && hasChildren) {
            return () => import("@/components/ParentView/index.vue");
        }
        // 默认返回404页面
        return () => import("@/views/NotFoundPage.vue");
    }

    function processMenu(menu) {
        // 只处理有路径的菜单项
        if (menu.path) {
            let component;

            // 根据menuType和component字段决定组件导入方式
            if (menu.component) {
                // 有指定组件，支持字符串路径或对象组件
                component = dynamicImportComponent(menu.component);
            } else {
                // 没有指定组件路径，根据菜单类型和是否有子菜单决定默认组件
                component = getDefaultComponent(
                    menu.menuType,
                    menu.children && menu.children.length > 0,
                );
            }

            if (component) {
                // 动态路由下统一使用相对路径，便于作为 AdminLayout 的子路由
                const normalizePath = (p) => {
                    if (!p) return p;
                    return isDynamic && p.startsWith("/AdminLayout")
                        ? p.slice(1)
                        : p;
                };

                const route = {
                    path: normalizePath(menu.path),
                    name: menu.perms || `menu_${menu.id}`,
                    component,
                    meta: {
                        title: menu.menuName,
                        requiresAuth: isDynamic, // 动态路由需要认证，静态路由不需要
                        menuType: menu.menuType,
                        perms: menu.perms,
                        icon: menu.icon,
                        orderNum: menu.orderNum || 0,
                        menuId: menu.id,
                        parentId: menu.parentId,
                    },
                };

                // 根据路由类型添加标记
                if (isDynamic) {
                    route.meta.isDynamic = true;
                } else {
                    route.meta.isNotLogRoute = true;
                }

                // 处理子路由
                if (menu.children && menu.children.length > 0) {
                    // 如果当前节点是 AdminLayout，则将其所有后代扁平化为单层子路由
                    const isAdminLayoutNode = menu.component === AdminLayout;
                    if (isAdminLayoutNode) {
                        const flatChildren = [];
                        const collectDescendants = (node) => {
                            if (!node.children || node.children.length === 0)
                                return;
                            node.children.forEach((child) => {
                                const childRoute = processMenu(child);
                                if (childRoute) {
                                    if (Array.isArray(childRoute)) {
                                        // 若递归返回数组，继续展开
                                        flatChildren.push(...childRoute);
                                    } else {
                                        // 子路由不再保留多层 children，确保只一层
                                        childRoute.children = undefined;
                                        flatChildren.push(childRoute);
                                    }
                                }
                                // 继续深入后代
                                collectDescendants(child);
                            });
                        };
                        collectDescendants(menu);
                        route.children = flatChildren;
                    } else {
                        // 常规处理：保持原有层级
                        route.children = [];
                        menu.children.forEach((child) => {
                            const childRoutes = processMenu(child);
                            if (childRoutes) {
                                if (Array.isArray(childRoutes)) {
                                    route.children.push(...childRoutes);
                                } else {
                                    route.children.push(childRoutes);
                                }
                            }
                        });
                    }
                }

                return route;
            }
        }

        // 如果当前菜单没有路径但有子菜单，递归处理子菜单
        if (menu.children && menu.children.length > 0) {
            const childRoutes = [];
            menu.children.forEach((child) => {
                const childRoute = processMenu(child);
                if (childRoute) {
                    if (Array.isArray(childRoute)) {
                        childRoutes.push(...childRoute);
                    } else {
                        childRoutes.push(childRoute);
                    }
                }
            });
            return childRoutes;
        }

        return null;
    }

    menuTree.forEach((menu) => {
        const route = processMenu(menu);
        if (route) {
            if (Array.isArray(route)) {
                routes.push(...route);
            } else {
                routes.push(route);
            }
        }
    });

    // 如果是动态路由，将所有路由统一包裹到 AdminLayout 的单一父级下，使用空路径以保持 URL 不变
    if (isDynamic) {
        const adminShellRoute = {
            path: "",
            name: "AdminLayoutShell",
            component: AdminLayout,
            // 不参与导航菜单展示
            meta: { isDynamicWrapper: true },
            children: routes,
        };
        return [adminShellRoute];
    }

    return routes;
}

/**
 * 统一的菜单转换函数
 * 将菜单数据（一维数组或嵌套数组）转换为路由配置
 * @param {Array} menuData - 菜单数据
 * @param {Object} options - 转换选项
 * @param {boolean} options.isDynamic - 是否为动态路由
 * @returns {Array} 路由配置数组
 */
export function convertMenuToRoutes(menuData, options = {}) {
    try {
        if (!Array.isArray(menuData) || menuData.length === 0) {
            console.warn("菜单数据为空或格式不正确");
            return [];
        }

        // 处理嵌套数组结构
        let flatMenuList = [];
        if (Array.isArray(menuData[0])) {
            flatMenuList = menuData[0]; // 取出内层数组
        } else {
            flatMenuList = menuData;
        }

        // 检查是否需要构建树形结构
        // 如果存在parentId不为0的菜单项，说明需要构建父子关系
        const needsBuildTree = flatMenuList.some(
            (menu) =>
                menu.parentId &&
                menu.parentId !== 0 &&
                menu.parentId !== null &&
                menu.parentId !== undefined,
        );

        let menuTree;
        if (needsBuildTree) {
            // 需要构建树形结构
            menuTree = buildMenuTree(flatMenuList);
        } else {
            // 已经是正确的结构，直接使用
            menuTree = flatMenuList;
        }

        // 将树形结构转换为路由
        const routes = convertMenuTreeToRoutes(menuTree, options);

        // 生产环境移除详细日志，避免性能影响
        if (import.meta.env.DEV) {
            console.log("菜单转换完成:", {
                原始数据: menuData,
                扁平数据: flatMenuList,
                树形结构: menuTree,
                路由配置: routes,
            });
        }

        return routes;
    } catch (error) {
        console.error("菜单转换失败:", error);
        return [];
    }
}

/**
 * 扁平化菜单树结构
 * @param {Array} menuTree - 树形结构的菜单数组
 * @returns {Array} 扁平化的菜单数组
 */
export function flattenMenuTree(menuTree) {
    const result = [];

    function flatten(menuList) {
        menuList.forEach((menu) => {
            result.push(menu);
            if (menu.children && menu.children.length > 0) {
                flatten(menu.children);
            }
        });
    }

    flatten(menuTree);
    return result;
}

/**
 * 根据路径查找菜单项
 * @param {Array} menuTree - 树形结构的菜单数组
 * @param {string} path - 要查找的路径
 * @returns {Object|null} 找到的菜单项或null
 */
export function findMenuByPath(menuTree, path) {
    function findInMenus(menuList, targetPath) {
        for (const menu of menuList) {
            if (menu.path === targetPath) {
                return menu;
            }
            if (menu.children && menu.children.length > 0) {
                const found = findInMenus(menu.children, targetPath);
                if (found) return found;
            }
        }
        return null;
    }

    return findInMenus(menuTree, path);
}

/**
 * 获取面包屑导航
 * @param {Array} menuTree - 树形结构的菜单数组
 * @param {string} currentPath - 当前路径
 * @returns {Array} 面包屑数组
 */
export function getBreadcrumb(menuTree, currentPath) {
    const breadcrumb = [];

    function findPath(menuList, targetPath, path = []) {
        for (const menu of menuList) {
            const currentPath = [...path, menu];
            if (menu.path === targetPath) {
                breadcrumb.push(...currentPath);
                return true;
            }
            if (menu.children && menu.children.length > 0) {
                if (findPath(menu.children, targetPath, currentPath)) {
                    return true;
                }
            }
        }
        return false;
    }

    findPath(menuTree, currentPath);
    return breadcrumb;
}
/**
 * 模块功能：菜单数据到路由的统一转换工具
 * 依赖：vue-router，项目组件 AdminLayout
 * 使用场景：后端返回菜单需转为前端路由；本改动将动态路由统一包裹在 AdminLayout 下，确保所有页面内容渲染在同一个 router-view 中。
 */
import AdminLayout from "@/components/AdminLayout.vue";
